import {
  filterGalleryAssets,
  filterVariantAssets,
  sortGalleryAssetsByLayer,
  PRODUCT_VIEW_LAYER,
} from '../../../components/AssetTargetsDialog/helpers/assetProductLinks';
import { LISTING_STATUS } from '../constants/listingStatus';

export const listingStatusToViewLayer = (listingStatus) => {
  const status = String(listingStatus || '').toUpperCase();
  if (status === LISTING_STATUS.DRAFT) return PRODUCT_VIEW_LAYER.STAGING;
  if (status === LISTING_STATUS.PUBLISHED) return PRODUCT_VIEW_LAYER.LIVE;
  return null;
};

export const splitAssetsByMasterProducts = (assets, productIds) => {
  const result = {};

  productIds.forEach((productId) => {
    const filtered = filterGalleryAssets(assets, {
      productId,
      layer: PRODUCT_VIEW_LAYER.MASTER,
    });
    result[productId] = sortGalleryAssetsByLayer(filtered, {
      productId,
      layer: PRODUCT_VIEW_LAYER.MASTER,
    });
  });

  return result;
};

export const splitAssetsByMasterVariants = (assets, variantEntries) => {
  const result = {};

  variantEntries.forEach(({ variantId, productId }) => {
    const filtered = filterVariantAssets(assets, {
      linkVariantId: variantId,
      variantType: 'masterVariant',
      layer: PRODUCT_VIEW_LAYER.MASTER,
    });
    result[variantId] = sortGalleryAssetsByLayer(filtered, {
      productId,
      layer: PRODUCT_VIEW_LAYER.MASTER,
    });
  });

  return result;
};

export const splitAssetsByListingProducts = (assets, listingProductEntries) => {
  const result = {};

  listingProductEntries.forEach(({ listingProductId, listingStatus }) => {
    const layer = listingStatusToViewLayer(listingStatus);
    if (!layer) {
      result[listingProductId] = [];
      return;
    }
    const filtered = filterGalleryAssets(assets, {
      productId: listingProductId,
      layer,
    });
    result[listingProductId] = sortGalleryAssetsByLayer(filtered, {
      productId: listingProductId,
      layer,
    });
  });

  return result;
};

export const splitAssetsByListingVariants = (assets, listingVariantEntries) => {
  const result = {};

  listingVariantEntries.forEach(({ listingProductId, variantId, listingStatus }) => {
    const layer = listingStatusToViewLayer(listingStatus);
    if (!layer) {
      result[variantId] = [];
      return;
    }
    const filtered = filterVariantAssets(assets, {
      linkVariantId: variantId,
      variantType: 'listingVariant',
      layer,
    });
    result[variantId] = sortGalleryAssetsByLayer(filtered, {
      productId: listingProductId,
      layer,
    });
  });

  return result;
};

export const BULK_MEDIA_PAGE_SIZE = 50;
export const BULK_SELECTION_SEARCH_SIZE_MAX = 500;

export const computeBulkSelectionSearchSize = () => BULK_MEDIA_PAGE_SIZE;

export const collectBulkSelectionSearchTargets = (pendingFetches = []) => {
  const masterProductIds = [];
  const masterVariantEntries = [];
  const listingProductEntries = [];
  const listingVariantEntries = [];
  const seenProducts = new Set();
  const seenVariants = new Set();
  const seenListings = new Set();
  const seenListingVariants = new Set();

  pendingFetches.forEach(({
    type, productId, variantId, listingProductId, listingStatus,
  }) => {
    if (type === 'master' && productId && !seenProducts.has(productId)) {
      seenProducts.add(productId);
      masterProductIds.push(productId);
      return;
    }

    if (type === 'masterVariant' && productId && variantId && !seenVariants.has(variantId)) {
      seenVariants.add(variantId);
      masterVariantEntries.push({ productId, variantId });
      return;
    }

    if (type === 'listing' && listingProductId && !seenListings.has(listingProductId)) {
      seenListings.add(listingProductId);
      listingProductEntries.push({ listingProductId, listingStatus });
      return;
    }

    if (type === 'listingVariant' && variantId && !seenListingVariants.has(variantId)) {
      seenListingVariants.add(variantId);
      listingVariantEntries.push({ listingProductId, variantId, listingStatus });
    }
  });

  return {
    masterProductIds,
    masterVariantEntries,
    listingProductEntries,
    listingVariantEntries,
  };
};

export const collectSearchTargetsFromSelection = (selection) => {
  const pendingFetches = [];
  let listingStatusFromContext;
  if (selection?.context?.recordState === 'draft') {
    listingStatusFromContext = LISTING_STATUS.DRAFT;
  } else if (selection?.context?.recordState === 'published') {
    listingStatusFromContext = LISTING_STATUS.PUBLISHED;
  }

  (selection?.keys || []).forEach((key) => {
    const entry = selection.byKey?.[key];
    if (!entry) return;

    if (entry.type === 'master') {
      pendingFetches.push({ type: 'master', productId: entry._id });
      return;
    }

    if (entry.type === 'masterVariant') {
      pendingFetches.push({
        type: 'masterVariant',
        productId: entry.baseProductId,
        variantId: entry._id,
      });
      return;
    }

    const listingStatus = entry.listingStatus || listingStatusFromContext;

    if (entry.type === 'listing') {
      pendingFetches.push({
        type: 'listing',
        listingProductId: entry._id,
        listingStatus,
      });
      return;
    }

    if (entry.type === 'listingVariant') {
      pendingFetches.push({
        type: 'listingVariant',
        listingProductId: entry.baseProductId,
        variantId: entry._id,
        listingStatus,
      });
    }
  });

  return collectBulkSelectionSearchTargets(pendingFetches);
};
